import type { Part } from "@opencode-ai/sdk/v2/client"
import type { ServerEvent } from "@/context/server-sdk"
import type { WorkflowScope } from "./workflow-status"
import { workflowToolStage } from "./workflow-status"

export const workflowResultTabs = ["files", "categories", "extractions", "transformations", "output", "report", "dashboard", "approval"] as const
export type WorkflowResultTab = typeof workflowResultTabs[number]
type SavedRecord = { id: string; project_id: string; period_id?: string | null; case_id?: string | null; status: string }

export function workflowTabCompletion(input: {
  scope: WorkflowScope
  files?: SavedRecord[]
  categories?: { extraction_id: string; status: string; planned?: boolean }[]
  extractions?: SavedRecord[]
  transformations?: (SavedRecord & { output_json: Record<string, unknown> })[]
  artifacts?: (SavedRecord & { artifact_type: string; artifact_metadata: Record<string, unknown> })[]
  dashboards?: (SavedRecord & { draft_config: Record<string, unknown>; published_config?: Record<string, unknown> | null })[]
  approvals?: { report_artifact_id: string; status: string }[]
}) {
  const scoped = (record: SavedRecord) => record.project_id === input.scope.projectID &&
    (record.period_id ?? undefined) === input.scope.periodID && (record.case_id ?? undefined) === input.scope.caseID
  const files = input.files?.filter(scoped) ?? []
  const extractions = input.extractions?.filter((record) => scoped(record) && record.status === "completed") ?? []
  const transformations = input.transformations?.filter((record) => scoped(record) && record.status === "completed") ?? []
  const reports = input.artifacts?.filter((record) => scoped(record) && record.artifact_type === "report" && record.artifact_metadata.source !== "dashboard_share" &&
    ["created", "completed", "generated", "published", "approved", "draft"].includes(record.status)) ?? []
  const approvals = input.approvals?.filter((approval) => reports.some((report) => report.id === approval.report_artifact_id)) ?? []
  return {
    files: files.length > 0 && files.every((file) => file.status === "uploaded"),
    categories: !!input.categories?.some((category) => !category.planned && category.status === "completed" && extractions.some((record) => record.id === category.extraction_id)),
    extractions: extractions.length > 0,
    transformations: transformations.length > 0,
    output: transformations.some((record) => {
      const tables = record.output_json.output_tables ?? (!record.output_json.pipeline_run_id ? record.output_json.tables : undefined)
      const values = Array.isArray(tables) ? tables : tables && typeof tables === "object" ? Object.values(tables) : []
      return values.some((table) => table && typeof table === "object" && table.visibility !== "internal" && (Array.isArray(table.rows) || Array.isArray(table.columns) || Array.isArray(table.headers)))
    }),
    report: reports.length > 0,
    dashboard: !!input.dashboards?.some((record) => {
      if (!scoped(record) || !["draft", "published", "completed"].includes(record.status)) return false
      return [record.draft_config, record.published_config].some((config) => Array.isArray(config?.tabs) && config.tabs.some((tab) =>
        tab && typeof tab === "object" && Array.isArray(tab.widgets) && tab.widgets.length > 0))
    }),
    approval: approvals.length > 0 && approvals.every((approval) => approval.status === "approved"),
  }
}

export function workflowTabsForStage(stage: string): WorkflowResultTab[] {
  if (stage === "reviewing") return ["files"]
  if (stage === "extracting" || stage === "savingExtraction") return ["categories", "extractions"]
  if (stage === "analysing" || stage === "savingOutputs") return ["transformations", "output"]
  if (stage === "generatingReport" || stage === "savingReport") return ["report"]
  if (stage === "buildingDashboard" || stage === "savingDashboard") return ["dashboard"]
  return []
}

export function workflowSavedResultEvent(event: ServerEvent, parts: Record<string, Part[]>) {
  const current = event.current
  const part = current?.type === "session.tool.success"
    ? parts[current.data.assistantMessageID]?.find((part) => part.type === "tool" && part.callID === current.data.callID)
    : event.type === "message.part.updated" ? event.properties.part : undefined
  if (part?.type !== "tool" || part.state.status !== "completed") return undefined
  const stage = workflowToolStage(part.tool, part.state.input)
  if (!stage || !["savingExtraction", "savingOutputs", "savingReport", "savingDashboard"].includes(stage)) return undefined
  return { sessionID: part.sessionID, key: `${part.sessionID}:${part.callID}:${part.state.time.end}`, tabs: workflowTabsForStage(stage) }
}
